import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { User, Lock, Save, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Settings = () => {
    const { user, token, login } = useAuth();
    const [email, setEmail] = useState(user?.email || '');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const handleUpdate = async (e) => {
        e.preventDefault();
        setMessage(null);

        if (password && password !== confirmPassword) {
            return setMessage({ type: 'error', text: 'Passwords do not match' });
        }

        setLoading(true);
        try {
            const payload = { email };
            if (password) payload.password = password;

            const res = await axios.patch('http://localhost:4000/api/auth/profile', payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Update local user context with new data
            if (res.data.status === 'success') {
                const newUser = res.data.data.user;
                // We keep the same token but update the user object
                login(token, newUser);
                setMessage({ type: 'success', text: 'Profile updated successfully' });
                setPassword('');
                setConfirmPassword('');
            }
        } catch (error) {
            console.error(error);
            setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update profile' });
        } finally {
            setLoading(false);
        }
    };

    /*
    const handleDownloadReport = async () => {
        try {
            // @ts-ignore
            const res = await axios.get('http://localhost:4000/api/reports/emergency-sheet', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Emergency_Sheet.pdf');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link); // Clean up the DOM
        } catch (error) {
            console.error("Failed to download report", error);
        }
    };

    const handleBackup = async () => {
        try {
             // @ts-ignore
            const res = await axios.get('http://localhost:4000/api/admin/export', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `backup_${Date.now()}.zip`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link); // Clean up the DOM
        } catch (error) {
            console.error("Failed to download backup", error);
        }
    }
    */

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
                    <p className="text-gray-500 mt-2">Manage your account, preferences, and data.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Sidebar / Info Card */}
                    <motion.div 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 h-fit"
                    >
                        <div className="flex flex-col items-center text-center">
                            <div className="h-24 w-24 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 flex items-center justify-center text-white text-3xl font-bold mb-4 shadow-lg ring-4 ring-white dark:ring-gray-700">
                                {user?.email ? user.email[0].toUpperCase() : 'U'}
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{user?.email?.split('@')[0]}</h2>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-2">
                                <Shield className="w-3 h-3 mr-1" />
                                {user?.role || 'Member'}
                            </span>
                            <p className="text-sm text-gray-500 mt-4 px-4">
                                Your account is secured with AES-256 encryption for all document uploads.
                            </p>
                        </div>
                    </motion.div>

                    {/* Main Settings Form */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-gray-700"
                    >
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                            <User className="w-5 h-5 mr-2 text-blue-500" />
                            Profile Details
                        </h3>

                        <form onSubmit={handleUpdate} className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Email Address</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full pl-10 rounded-xl border-gray-200 bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600 p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                    />
                                </div>
                            </div>

                            <hr className="border-gray-100 dark:border-gray-700" />

                            <div>
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                    <Lock className="w-4 h-4 mr-2 text-blue-500" />
                                    Change Password
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">New Password</label>
                                        <input
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Leave blank to keep current"
                                            className="w-full rounded-xl border-gray-200 bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600 p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">Confirm Password</label>
                                        <input
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Confirm new password"
                                            className="w-full rounded-xl border-gray-200 bg-gray-50 dark:bg-gray-700/50 dark:border-gray-600 p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        />
                                    </div>
                                </div>
                            </div>

                            <AnimatePresence>
                                {message && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className={`p-4 rounded-xl flex items-center ${
                                            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                                        }`}
                                    >
                                        {message.type === 'success' ? (
                                            <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                                        ) : (
                                            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                                        )}
                                        <span className="text-sm font-medium">{message.text}</span>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="flex justify-end pt-4">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="inline-flex items-center justify-center py-3 px-8 border border-transparent text-sm font-bold rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all hover:scale-[1.02] active:scale-95 shadow-lg"
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4 mr-2" />
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            </div>
        </Layout>
    );
};

export default Settings;
